import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Container, Badge, Accordion, Table, Modal, Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getVendorAssessmentService, submitVendorAssessmentService, getAssessmentResponsesService, getAssessmentDetailsService, reviewResponseService, updateAssessmentReviewStatusService, uploadVendorDocumentsService, getVendorQuestionSuggestionsService, getTprmAssessmentsService, uploadVendorResponseDocumentsService, deleteVendorResponseDocumentsService } from '../../services';
import FullPageLoader from '../../Components/FullPageLoader';
import { formatText } from '../../helper';
import CommonTable from '../../Components/CommonTable';

function VendorAssessmentView() {
    const { access_token, assessment_id } = useParams();
    const [loading, setLoading] = useState(true);
    const [assessment, setAssessment] = useState(null);
    const [adminQuestions, setAdminQuestions] = useState([]);
    const [orgQuestions, setOrgQuestions] = useState([]);
    const [assessmentResponses, setAssessmentResponses] = useState([]);
    const [existingResponses, setExistingResponses] = useState([]);
    const [error, setError] = useState('');
    const [responses, setResponses] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // const [saveMessage, setSaveMessage] = useState(''); // Removed in favor of toast
    const [isViewMode, setIsViewMode] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showAssessmentReviewModal, setShowAssessmentReviewModal] = useState(false);
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [reviewNotes, setReviewNotes] = useState('');
    const [reviewerType, setReviewerType] = useState('org');
    const [acceptanceStatus, setAcceptanceStatus] = useState('pending');
    const [reviewing, setReviewing] = useState(false);
    const [assessmentReviewStatus, setAssessmentReviewStatus] = useState('completed');
    const [assessmentReviewNotes, setAssessmentReviewNotes] = useState('');
    const [assessmentGrade, setAssessmentGrade] = useState('');
    const [acceptanceFilter, setAcceptanceFilter] = useState('All');
    const [updatingAssessmentStatus, setUpdatingAssessmentStatus] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedSubCategory, setSelectedSubCategory] = useState('All');
    const [completionFilter, setCompletionFilter] = useState('All');
    const [showOnlyAiSuggested, setShowOnlyAiSuggested] = useState(false);
    const [focusedQuestionId, setFocusedQuestionId] = useState(null);

    // Bulk file upload states
    const [bulkFiles, setBulkFiles] = useState([]);
    const [uploadingBulkFiles, setUploadingBulkFiles] = useState(false);
    const [bulkUploadMessage, setBulkUploadMessage] = useState('');
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

    // AI Suggestions states
    const [aiSuggestions, setAiSuggestions] = useState([]);
    const [documentUploads, setDocumentUploads] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [processingStatus, setProcessingStatus] = useState(null);

    // Toast notification state
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Previous Assessment modal states
    const [showPreviousAssessmentModal, setShowPreviousAssessmentModal] = useState(false);
    const [previousAssessments, setPreviousAssessments] = useState([]);
    const [loadingPreviousAssessments, setLoadingPreviousAssessments] = useState(false);

    // Per-response document upload states
    const [uploadingResponseId, setUploadingResponseId] = useState(null);
    const [deletingDocumentId, setDeletingDocumentId] = useState(null);
    const [uploadResponseMessage, setUploadResponseMessage] = useState({ id: null, text: '', type: '' });

    // Tutorial state
    const [tutorialStep, setTutorialStep] = useState(null);
    const [cardPos, setCardPos] = useState({ top: 0, left: 0, arrowLeft: '50%' });

    const TUTORIAL_STEPS = [
        { step: 1, target: 'tut-upload-btn', title: 'Upload your documents', desc: 'Start by uploading company documents — policies, certifications, reports. The AI will read them to suggest answers.' },
        { step: 2, target: 'tut-ai-btn', title: 'Get AI-suggested answers', desc: 'Click "View AI Suggestions" to auto-fill your questionnaire based on the uploaded documents.' },
        { step: 3, target: 'tut-questions', title: 'Complete & submit', desc: 'Review the auto-filled answers, complete any remaining questions, then hit Submit.' },
    ];

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
    };

    const advanceTutorial = () => {
        if (tutorialStep < 3) {
            setTutorialStep(prev => prev + 1);
        } else {
            closeTutorial();
        }
    };

    const closeTutorial = () => {
        setTutorialStep(null);
        if (access_token) localStorage.setItem(`vav_tutorial_${access_token}`, '1');
    };

    // File upload constraints (per API: max 5 files, 28MB total)
    const MAX_FILES = 5;
    const MAX_TOTAL_SIZE = 28 * 1024 * 1024; // 28MB total in bytes
    const MAX_FILE_SIZE = 28 * 1024 * 1024; // 28MB max per validation
    const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];

    useEffect(() => {
        if (assessment_id) {
            // View responses mode (authenticated)
            setIsViewMode(true);
            fetchAssessmentResponses();
        } else if (access_token) {
            // Vendor assessment mode (public)
            setIsViewMode(false);
            fetchAssessment();
        }
    }, [access_token, assessment_id]);

    // Show tutorial on first visit (vendor mode only)
    useEffect(() => {
        if (!isViewMode && access_token && !loading) {
            const key = `vav_tutorial_${access_token}`;
            if (!localStorage.getItem(key)) {
                setTutorialStep(1);
            }
        }
    }, [loading]);

    // Disable scroll on step 3, re-enable on dismiss
    useEffect(() => {
        const lockScroll = (e) => e.preventDefault();
        if (tutorialStep === 3) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            window.addEventListener('wheel', lockScroll, { passive: false });
            window.addEventListener('touchmove', lockScroll, { passive: false });
        } else {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            window.removeEventListener('wheel', lockScroll);
            window.removeEventListener('touchmove', lockScroll);
        }
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            window.removeEventListener('wheel', lockScroll);
            window.removeEventListener('touchmove', lockScroll);
        };
    }, [tutorialStep]);

    // Position tutorial card near the target element
    useEffect(() => {
        if (!tutorialStep) return;
        const step = TUTORIAL_STEPS[tutorialStep - 1];
        const el = document.getElementById(step.target);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const CARD_WIDTH = 300;
        // For step 3 (large section), anchor to the top-left of the section
        // For steps 1 & 2 (buttons), anchor below the button centered on it
        // Step 3: card sits ABOVE the questions section, arrow points DOWN
        const arrowDown = tutorialStep === 3;
        const anchorX = rect.left + (tutorialStep === 3 ? 160 : rect.width / 2);
        const anchorY = arrowDown
            ? Math.max(70, rect.top - 205)  // above section, clamped below navbar
            : rect.bottom + 14;             // below button
        let left = anchorX;
        left = Math.max(CARD_WIDTH / 2 + 16, Math.min(left, window.innerWidth - CARD_WIDTH / 2 - 16));
        const arrowLeft = `${anchorX - (left - CARD_WIDTH / 2)}px`;
        setCardPos({ top: anchorY, left, arrowLeft, arrowDown });
    }, [tutorialStep]);

    const fetchAssessmentResponses = async (silent = false) => {
        try {
            if (!silent) setLoading(true);

            // Fetch both assessment details and responses in parallel
            const [detailsResponse, responsesResponse] = await Promise.all([
                getAssessmentDetailsService(assessment_id),
                getAssessmentResponsesService(assessment_id)
            ]);

            // Process assessment details
            const detailsData = detailsResponse?.statusCode?.response_data?.data || detailsResponse?.data;
            if (detailsData) {
                setAssessment(detailsData);
            }

            // Process responses
            const responsesData = responsesResponse?.statusCode?.response_data?.data || responsesResponse?.data;
            if (responsesData) {
                setAssessmentResponses(Array.isArray(responsesData.responses) ? responsesData.responses : []);
            } else {
                setError('Failed to load assessment responses.');
            }
        } catch (err) {
            console.error('Error fetching assessment data:', err);
            setError('Failed to load assessment data. Please try again.');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const fetchAssessment = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const response = await getVendorAssessmentService(access_token);

            const data =
                response?.statusCode?.response_data?.data ||
                response?.data;

            if (!data) {
                setError('Failed to load assessment.');
                return;
            }

            const {
                assessment,
                admin_questions = [],
                org_questions = [],
                ee_questions = [],
                existing_responses = []
            } = data;

            // 🔥 PRE-FILL RESPONSES (THIS WAS MISSING)
            const prefilledResponses = {};

            existing_responses.forEach(res => {
                if (res.vendor_question) {
                    prefilledResponses[`admin_${res.vendor_question}`] =
                        res.response_text?.includes(',')
                            ? res.response_text.split(',').map(v => v.trim())
                            : res.response_text;
                    if (res.explanation) {
                        prefilledResponses[`admin_text_${res.vendor_question}`] = res.explanation;
                    }
                }

                if (res.org_vendor_question) {
                    prefilledResponses[`org_${res.org_vendor_question}`] =
                        res.response_text?.includes(',')
                            ? res.response_text.split(',').map(v => v.trim())
                            : res.response_text;
                    if (res.explanation) {
                        prefilledResponses[`org_text_${res.org_vendor_question}`] = res.explanation;
                    }
                }
            });

            setExistingResponses(existing_responses);
            setResponses(prefilledResponses);
            setAssessment(assessment || {});
            setAdminQuestions([...admin_questions, ...ee_questions]);
            setOrgQuestions(org_questions);

        } catch (err) {
            console.error(err);
            if (!silent) setError('Failed to load assessment.');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'sent': 'info',
            'in_progress': 'warning',
            'submitted': 'primary',
            'completed': 'success',
            'expired': 'dark',
            'rejected': 'danger'
        };
        return statusColors[status] || 'secondary';
    };

    const handleReviewResponse = (response) => {
        console.log('Review button clicked', response);
        setSelectedResponse(response);
        setReviewNotes(response.reviewer_notes || '');
        setShowReviewModal(true);
        console.log('Modal state set to true');
    };

    const handleSubmitReview = async () => {
        if (!selectedResponse) return;
        if (assessment?.status !== 'submitted') {
            showToast('Assessment must be submitted to review responses.', 'warning');
            return;
        }

        setReviewing(true);
        try {
            const response = await reviewResponseService(selectedResponse.id, {
                reviewer_type: reviewerType,
                acceptance_status: acceptanceStatus,
                review_notes: reviewNotes
            });

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.statusCode?.message ||
                response?.data?.message;

            if (successMessage) {
                // Refresh responses
                fetchAssessmentResponses(true);
                setShowReviewModal(false);
                setSelectedResponse(null);
                setReviewNotes('');
                setReviewerType('org');
                setAcceptanceStatus('pending');
                showToast(successMessage || 'Response reviewed successfully!', 'success');
            } else {
                showToast('Failed to review response. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error reviewing response:', error);
            showToast('Failed to review response. Please try again.', 'error');
        } finally {
            setReviewing(false);
        }
    };

    const handleUpdateAssessmentStatus = async () => {
        if (!assessment_id) return;

        setUpdatingAssessmentStatus(true);
        try {
            const params = {
                action: assessmentReviewStatus,
                reason: assessmentReviewNotes
            };
            if (['approved', 'rejected'].includes(assessmentReviewStatus) && assessmentGrade) {
                params.grade = assessmentGrade;
            }
            const response = await updateAssessmentReviewStatusService(assessment_id, params);

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.statusCode?.message ||
                response?.data?.message;

            if (successMessage) {
                // Refresh assessment details and responses
                fetchAssessmentResponses(true);
                setShowAssessmentReviewModal(false);
                setAssessmentReviewStatus('completed');
                setAssessmentReviewNotes('');
                setAssessmentGrade('');
                showToast(successMessage || 'Assessment review status updated successfully!', 'success');
            } else {
                showToast('Failed to update assessment status. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error updating assessment status:', error);
            showToast('Failed to update assessment status. Please try again.', 'error');
        } finally {
            setUpdatingAssessmentStatus(false);
        }
    };

    // Bulk file upload functions
    const validateFile = (file) => {
        const errors = [];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        // Check file extension
        if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
            errors.push(`Invalid format: ${file.name}. Allowed: PDF, DOC, DOCX, XLS, XLSX, CSV`);
        }

        // Check file size
        if (file.size > MAX_FILE_SIZE) {
            errors.push(`File too large: ${file.name}. Max size: 28MB`);
        }

        return errors;
    };

    const handleBulkFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const allErrors = [];
        const validFiles = [];

        // Check total file count
        if (bulkFiles.length + selectedFiles.length > MAX_FILES) {
            showToast(`Maximum ${MAX_FILES} files allowed. You already have ${bulkFiles.length} files selected.`, 'error');
            e.target.value = '';
            return;
        }

        // Check total file size across all files
        const currentTotalSize = bulkFiles.reduce((sum, f) => sum + f.size, 0);
        const selectedTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
        if (currentTotalSize + selectedTotalSize > MAX_TOTAL_SIZE) {
            showToast(`Total file size across all files cannot exceed 28MB. Current: ${formatFileSize(currentTotalSize)}, New: ${formatFileSize(selectedTotalSize)}`, 'error');
            e.target.value = '';
            return;
        }

        selectedFiles.forEach(file => {
            const errors = validateFile(file);
            if (errors.length > 0) {
                allErrors.push(...errors);
            } else {
                // Check for duplicate files
                const isDuplicate = bulkFiles.some(f => f.name === file.name && f.size === file.size);
                if (!isDuplicate) {
                    validFiles.push(file);
                } else {
                    allErrors.push(`Duplicate file: ${file.name}`);
                }
            }
        });

        if (allErrors.length > 0) {
            showToast('Some files were not added: ' + allErrors.join(', '), 'error');
        }

        if (validFiles.length > 0) {
            setBulkFiles(prev => [...prev, ...validFiles]);
        }

        e.target.value = ''; // Reset input
    };

    const removeBulkFile = (index) => {
        setBulkFiles(prev => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleBulkUpload = async () => {
        if (bulkFiles.length === 0) {
            showToast('Please select at least one file to upload.', 'error');
            return;
        }

        if (!access_token) {
            showToast('Access token not found. Please try again.', 'error');
            return;
        }

        // Check total size
        const totalSize = bulkFiles.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
            showToast(`Total file size exceeds 28MB. Current total: ${formatFileSize(totalSize)}`, 'error');
            return;
        }

        setUploadingBulkFiles(true);
        setBulkUploadMessage('');

        try {
            const response = await uploadVendorDocumentsService(access_token, bulkFiles);

            const successMessage =
                response?.data?.message ||
                response?.statusCode?.response_data?.message ||
                response?.statusCode?.message ||
                'Documents uploaded successfully. AI processing started.';

            const uploadData = response?.data?.data || response?.statusCode?.response_data?.data;

            if (uploadData) {
                setProcessingStatus(uploadData.processing_status);
            }

            setBulkUploadMessage(successMessage);
            setBulkFiles([]);
            setShowBulkUploadModal(false);
            showToast(successMessage, 'success');

            // Fetch suggestions after upload
            fetchQuestionSuggestions();
        } catch (error) {
            console.error('Error uploading documents:', error);
            const errorMessage =
                error?.response?.data?.error_msg ||
                error?.response?.data?.message ||
                'Failed to upload files. Please try again.';
            setBulkUploadMessage(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setUploadingBulkFiles(false);
        }
    };

    // Fetch AI-generated question suggestions
    const fetchQuestionSuggestions = async (isManualClick = false) => {
        if (!access_token) return;

        setLoadingSuggestions(true);
        try {
            const response = await getVendorQuestionSuggestionsService(access_token);
            const data = response?.statusCode?.response_data?.data || response?.response_data?.data || response?.data;
            if (data) {
                const suggestions = data.suggestions || [];
                const uploads = data.document_uploads || [];

                setAiSuggestions(suggestions);
                setDocumentUploads(uploads);

                // Check if manually clicked and no documents — guide user back to step 1
                if (isManualClick && uploads.length === 0) {
                    setTutorialStep(1);
                }

                // Check processing status
                const processing = uploads.some(u => u.status === 'processing' || u.status === 'pending');
                setProcessingStatus(processing ? 'processing' : 'completed');
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setProcessingStatus('failed');
        } finally {
            setLoadingSuggestions(false);
        }
    };

    // Polling effect: call API every 7 seconds while processing
    useEffect(() => {
        let intervalId = null;

        if (processingStatus === 'processing') {
            intervalId = setInterval(() => {
                fetchQuestionSuggestions();
            }, 7000); // 7 seconds
        }

        // Cleanup interval on status change or unmount
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [processingStatus, access_token]);

    // Apply AI suggestion to a question
    const applySuggestion = (questionId, questionType, suggestionText, responseType = '') => {
        let key = `${questionType}_${questionId}`;

        // If question type is structured (yes/no, multi-select), apply suggestion to the text explanation field
        // Otherwise apply to the main response field
        if (['yes_no', 'yes/no', 'multi_select', 'multi-select'].includes((responseType || '').toLowerCase())) {
            key = `${questionType}_text_${questionId}`;
        }

        setResponses(prev => ({
            ...prev,
            [key]: suggestionText
        }));
    };


    const formatResponsesForAPI = () => {
        const apiResponses = [];

        // Process admin questions
        adminQuestions.forEach(question => {
            const key = `admin_${question.id}`;
            const textKey = `admin_text_${question.id}`;
            const fileKey = `admin_file_${question.id}`;
            const value = responses[key];
            const textValue = responses[textKey];
            const fileValue = responses[fileKey];

            if (value !== undefined && value !== null && value !== '' || textValue || fileValue) {
                let responseText = '';

                if (Array.isArray(value)) {
                    // Multi-select: join array values
                    responseText = value.join(', ');
                } else if (typeof value === 'object' && value instanceof File) {
                    // File upload: use file name
                    responseText = value.name;
                } else if (value) {
                    // Simple value (yes/no, text, rating)
                    responseText = String(value);
                }

                const responseObj = {
                    vendor_question: question.id,
                    response_text: responseText
                };

                if (textValue) {
                    responseObj.explanation = textValue;
                }
                if (fileValue && fileValue instanceof File) {
                    responseObj.response_file = fileValue;
                }

                apiResponses.push(responseObj);
            }
        });

        // Process org questions
        orgQuestions.forEach(question => {
            const key = `org_${question.id}`;
            const textKey = `org_text_${question.id}`;
            const fileKey = `org_file_${question.id}`;
            const value = responses[key];
            const textValue = responses[textKey];
            const fileValue = responses[fileKey];

            if (value !== undefined && value !== null && value !== '' || textValue || fileValue) {
                let responseText = '';

                if (Array.isArray(value)) {
                    // Multi-select: join array values
                    responseText = value.join(', ');
                } else if (typeof value === 'object' && value instanceof File) {
                    // File upload: use file name
                    responseText = value.name;
                } else if (value) {
                    // Simple value (yes/no, text, rating)
                    responseText = String(value);
                }

                const responseObj = {
                    org_vendor_question: question.id,
                    response_text: responseText
                };

                if (textValue) {
                    responseObj.explanation = textValue;
                }
                if (fileValue && fileValue instanceof File) {
                    responseObj.response_file = fileValue;
                }

                apiResponses.push(responseObj);
            }
        });

        return apiResponses;
    };

    const handleAutoSave = async () => {
        const apiResponses = formatResponsesForAPI();

        if (apiResponses.length === 0) {
            return; // No responses to save
        }

        setSaving(true);
        setSaving(true);

        try {
            const response = await submitVendorAssessmentService(access_token, {
                responses: apiResponses,
                submit: false
            });

            console.log('Auto-save Response:', response);
            showToast('Progress saved', 'success');
        } catch (error) {
            console.error('Error auto-saving:', error);
            showToast('Failed to save', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleFinalSubmit = async () => {
        const apiResponses = formatResponsesForAPI();

        if (apiResponses.length === 0) {
            showToast('Please answer at least one question before submitting.', 'error');
            return;
        }

        // Check ALL questions for explanation text
        const missingExplanations = [];

        adminQuestions.forEach(q => {
            const textKey = `admin_text_${q.id}`;
            const textValue = responses[textKey];
            if (!textValue || textValue.trim() === '') {
                missingExplanations.push(q);
            }
        });

        orgQuestions.forEach(q => {
            const textKey = `org_text_${q.id}`;
            const textValue = responses[textKey];
            if (!textValue || textValue.trim() === '') {
                missingExplanations.push(q);
            }
        });

        if (missingExplanations.length > 0) {
            showToast(`Please fill in the explanation for all questions. ${missingExplanations.length} explanation(s) are missing.`, 'error');
            return;
        }

        // Check required questions for answer + explanation
        const unansweredRequired = [];

        // Check admin questions
        adminQuestions.forEach(q => {
            if (q.is_required) {
                const key = `admin_${q.id}`;
                const textKey = `admin_text_${q.id}`;
                const value = responses[key];
                const textValue = responses[textKey];
                if (!value || value === '' || (Array.isArray(value) && value.length === 0) || !textValue || textValue.trim() === '') {
                    unansweredRequired.push(q);
                }
            }
        });

        // Check org questions
        orgQuestions.forEach(q => {
            if (q.is_required) {
                const key = `org_${q.id}`;
                const textKey = `org_text_${q.id}`;
                const value = responses[key];
                const textValue = responses[textKey];
                if (!value || value === '' || (Array.isArray(value) && value.length === 0) || !textValue || textValue.trim() === '') {
                    unansweredRequired.push(q);
                }
            }
        });

        if (unansweredRequired.length > 0) {
            showToast(`Please answer all required questions. ${unansweredRequired.length} required question(s) remaining.`, 'error');
            return;
        }

        setSubmitting(true);

        try {
            const response = await submitVendorAssessmentService(access_token, {
                responses: apiResponses,
                submit: true
            });

            console.log('Submit Response:', response);

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.statusCode?.message ||
                response?.data?.message ||
                'Assessment submitted successfully!';

            showToast(successMessage, 'success');

            // Refresh assessment data
            fetchAssessment();
        } catch (error) {
            console.error('Error submitting assessment:', error);
            showToast('Failed to submit assessment. Please try again.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResponseChange = (questionId, value, questionType = 'admin') => {
        setResponses(prev => ({
            ...prev,
            [`${questionType}_${questionId}`]: value
        }));
    };

    // Auto-save + refresh after response change (for radio/yes-no)
    const handleResponseChangeAndSave = async (questionId, value, questionType = 'admin') => {
        setResponses(prev => ({
            ...prev,
            [`${questionType}_${questionId}`]: value
        }));

        // Build response payload directly (don't rely on stale state)
        const responseObj = questionType === 'admin'
            ? { vendor_question: questionId, response_text: String(value) }
            : { org_vendor_question: questionId, response_text: String(value) };

        // Include existing explanation if any
        const textKey = `${questionType}_text_${questionId}`;
        if (responses[textKey]) {
            responseObj.explanation = responses[textKey];
        }

        try {
            await submitVendorAssessmentService(access_token, {
                responses: [responseObj],
                submit: false
            });
            // await fetchAssessment(true); // Don't refetch, just update UI
            showToast('Progress saved', 'success');
        } catch (error) {
            console.error('Error saving response:', error);
            showToast('Failed to save', 'error');
        }
    };

    const handleMultiSelectChange = (questionId, option, questionType = 'admin') => {
        const key = `${questionType}_${questionId}`;
        const currentValues = responses[key] || [];

        const newValues = currentValues.includes(option)
            ? currentValues.filter(v => v !== option)
            : [...currentValues, option];

        setResponses(prev => ({
            ...prev,
            [key]: newValues
        }));
    };

    // Auto-save + refresh after multi-select change
    const handleMultiSelectChangeAndSave = async (questionId, option, questionType = 'admin') => {
        const key = `${questionType}_${questionId}`;
        const currentValues = responses[key] || [];

        const newValues = currentValues.includes(option)
            ? currentValues.filter(v => v !== option)
            : [...currentValues, option];

        setResponses(prev => ({
            ...prev,
            [key]: newValues
        }));

        // Build response payload directly
        const responseText = newValues.join(', ');
        const responseObj = questionType === 'admin'
            ? { vendor_question: questionId, response_text: responseText }
            : { org_vendor_question: questionId, response_text: responseText };

        const textKey = `${questionType}_text_${questionId}`;
        if (responses[textKey]) {
            responseObj.explanation = responses[textKey];
        }

        try {
            await submitVendorAssessmentService(access_token, {
                responses: [responseObj],
                submit: false
            });
            // await fetchAssessment(true); // Don't refetch, just update UI
            showToast('Progress saved', 'success');
        } catch (error) {
            console.error('Error saving response:', error);
            showToast('Failed to save', 'error');
        }
    };

    const handleFileUpload = (questionId, file, questionType = 'admin') => {
        const key = `${questionType}_${questionId}`;
        setResponses(prev => ({
            ...prev,
            [key]: file
        }));
    };

    // Upload document to a specific response
    const handleResponseDocumentUpload = async (responseId, file) => {
        try {
            setUploadingResponseId(responseId);
            setUploadResponseMessage({ id: null, text: '', type: '' });
            await uploadVendorResponseDocumentsService(access_token, responseId, [file]);
            setUploadResponseMessage({ id: responseId, text: 'Document uploaded successfully!', type: 'success' });
            await fetchAssessment(true);
            setTimeout(() => setUploadResponseMessage({ id: null, text: '', type: '' }), 3000);
        } catch (error) {
            console.error('Error uploading document:', error);
            setUploadResponseMessage({ id: responseId, text: 'Failed to upload document.', type: 'error' });
            setTimeout(() => setUploadResponseMessage({ id: null, text: '', type: '' }), 3000);
        } finally {
            setUploadingResponseId(null);
        }
    };

    // Delete document from a specific response
    const handleDeleteDocument = async (responseId, documentId) => {
        try {
            setDeletingDocumentId(documentId);
            setUploadResponseMessage({ id: null, text: '', type: '' });
            await deleteVendorResponseDocumentsService(access_token, responseId, [documentId]);
            setUploadResponseMessage({ id: responseId, text: 'Document deleted successfully!', type: 'success' });
            await fetchAssessment(true);
            setTimeout(() => setUploadResponseMessage({ id: null, text: '', type: '' }), 3000);
        } catch (error) {
            console.error('Error deleting document:', error);
            setUploadResponseMessage({ id: responseId, text: 'Failed to delete document.', type: 'error' });
            setTimeout(() => setUploadResponseMessage({ id: null, text: '', type: '' }), 3000);
        } finally {
            setDeletingDocumentId(null);
        }
    };

    // Helper function to get review info for a question
    const getReviewInfoForQuestion = (questionId, questionType = 'admin') => {
        if (!existingResponses || existingResponses.length === 0) return null;

        let response = null;
        if (questionType === 'admin') {
            response = existingResponses.find(res => res.vendor_question === questionId);
        } else {
            response = existingResponses.find(res => res.org_vendor_question === questionId);
        }

        if (response && response.is_reviewed) {
            return {
                reviewed_by_name: response.reviewed_by_name,
                reviewer_notes: response.reviewer_notes,
                is_reviewed: response.is_reviewed
            };
        }
        return null;
    };

    const isFormDisabled = !['draft', 'in_progress', 'sent'].includes(assessment?.status);

    const renderInputField = (question, questionType = 'admin') => {
        const questionKey = `${questionType}_${question.id}`;
        const responseType = question.response_type || question.response_type_display || '';
        const currentValue = responses[questionKey];
        const textKey = `${questionType}_text_${question.id}`;
        const fileKey = `${questionType}_file_${question.id}`;

        // Render primary input based on response type
        const renderPrimaryInput = () => {
            switch (responseType.toLowerCase()) {
                case 'yes_no':
                case 'yes/no':
                    return (
                        <div className="mt-1">
                            <div className="w-100 d-flex gap-5" role="group">
                                <Form.Check
                                    type="radio"
                                    label="Yes"
                                    name={questionKey}
                                    id={`${questionKey}_yes`}
                                    checked={currentValue === 'yes'}
                                    onChange={() => handleResponseChangeAndSave(question.id, 'yes', questionType)}
                                    onFocus={() => setFocusedQuestionId(question.id)}
                                    onBlur={() => setFocusedQuestionId(null)}
                                    className="text-white"
                                    disabled={isFormDisabled}
                                />
                                <Form.Check
                                    type="radio"
                                    label="No"
                                    name={questionKey}
                                    id={`${questionKey}_no`}
                                    checked={currentValue === 'no'}
                                    onChange={() => handleResponseChangeAndSave(question.id, 'no', questionType)}
                                    onFocus={() => setFocusedQuestionId(question.id)}
                                    onBlur={() => setFocusedQuestionId(null)}
                                    className="text-white"
                                    disabled={isFormDisabled}
                                />
                            </div>
                        </div>
                    );

                case 'multi_select':
                case 'multi-select':
                    const options = question.options || question.multi_select_options || [];
                    return (
                        <div className="mt-1">
                            <small className="text-white d-block mb-2">Select all that apply:</small>
                            {options.map((option, idx) => (
                                <div key={idx} className="form-check mb-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`${questionKey}_${idx}`}
                                        checked={(currentValue || []).includes(option)}
                                        onChange={() => handleMultiSelectChangeAndSave(question.id, option, questionType)}
                                        onFocus={() => setFocusedQuestionId(question.id)}
                                        onBlur={() => setFocusedQuestionId(null)}
                                        disabled={isFormDisabled}
                                    />
                                    <label className="form-check-label text-white" htmlFor={`${questionKey}_${idx}`}>
                                        {option}
                                    </label>
                                </div>
                            ))}
                        </div>
                    );

                default:
                    return null;
            }
        };

        return (
            <div>
                {renderPrimaryInput()}

                {/* Text field for explanation */}
                <div className="mt-3">
                    <small className="text-white d-block mb-2">Explanation <span className="text-danger">*</span></small>
                    <textarea
                        className="form-control questionnaire_input"
                        rows="3"
                        required
                        placeholder="Enter your explanation or details here..."
                        value={responses[textKey] || ''}
                        onChange={(e) => {
                            setResponses(prev => ({
                                ...prev,
                                [textKey]: e.target.value
                            }));
                        }}
                        onFocus={() => setFocusedQuestionId(question.id)}
                        onBlur={async () => {
                            setFocusedQuestionId(null);
                            if (responses[textKey]?.trim()) {
                                await handleAutoSave();
                                await fetchAssessment(true);
                            }
                        }}
                        disabled={isFormDisabled}
                    />
                </div>

                {/* File upload field - always show, disable if no existing response */}
                {(() => {
                    // Find existing response for this question
                    const existingRes = existingResponses.find(res =>
                        questionType === 'admin' ? res.vendor_question === question.id : res.org_vendor_question === question.id
                    );

                    const hasResponseId = existingRes && existingRes.id;
                    const isUploading = hasResponseId ? uploadingResponseId === existingRes.id : false;
                    const msg = hasResponseId && uploadResponseMessage.id === existingRes.id ? uploadResponseMessage : null;

                    const isDisabled = isFormDisabled || isUploading || !hasResponseId;

                    return (
                        <div className="mt-3">
                            <div className="d-flex gap-4 align-items-center">
                                <small className="text-white">Upload Supporting Document: </small>
                                <input
                                    type="file"
                                    id={`response-file-${hasResponseId ? existingRes.id : question.id}`}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        if (hasResponseId && e.target.files[0]) {
                                            handleResponseDocumentUpload(existingRes.id, e.target.files[0]);
                                        }
                                    }}
                                    disabled={isDisabled}
                                />
                                {!hasResponseId ? (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-${question.id}`}>* Document upload will remain disabled until an answer is provided.</Tooltip>}
                                    >
                                        <span className="d-inline-block">
                                            <Button
                                                variant="outline-info"
                                                size="sm"
                                                as="label"
                                                title='* Document upload will remain disabled until an answer is provided.'
                                                htmlFor={`response-file-${hasResponseId ? existingRes.id : question.id}`}
                                                style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', pointerEvents: isDisabled ? 'none' : 'auto' }}
                                                disabled={isDisabled}
                                            >
                                                {isUploading ? (
                                                    <><span className="spinner-border spinner-border-sm me-1"></span> Uploading...</>
                                                ) : 'Upload'}
                                            </Button>
                                        </span>
                                    </OverlayTrigger>
                                ) : (
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        as="label"
                                        title='* Document upload will remain disabled until an answer is provided.'
                                        htmlFor={`response-file-${hasResponseId ? existingRes.id : question.id}`}
                                        style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', pointerEvents: isDisabled ? 'none' : 'auto' }}
                                        disabled={isDisabled}
                                    >
                                        {isUploading ? (
                                            <><span className="spinner-border spinner-border-sm me-1"></span> Uploading...</>
                                        ) : 'Upload'}
                                    </Button>
                                )}
                                {hasResponseId && existingRes.documents && existingRes.documents.length > 0 && (
                                    <small className="text-success">
                                        ✓ {existingRes.documents.length} document(s) uploaded
                                    </small>
                                )}
                            </div>



                            {hasResponseId && existingRes.documents && existingRes.documents.length > 0 && (
                                <div className="mt-2" style={{ paddingLeft: '10px' }}>
                                    {existingRes.documents.map((doc, dIdx) => (
                                        <div key={dIdx} className="d-flex align-items-center gap-2 mb-1">
                                            <img src="https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/add-on-notes.svg" alt="Document" style={{ width: '20px', height: '20px' }} />
                                            <a
                                                href={doc.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#60a5fa', fontSize: '13px', textDecoration: 'underline' }}
                                            >
                                                {doc.filename}
                                            </a>
                                            <small style={{ color: '#94a3b8', fontSize: '11px' }}>
                                                ({(doc.size / 1024).toFixed(1)} KB)
                                            </small>
                                            <small style={{ color: '#64748b', fontSize: '11px' }}>
                                                {new Date(doc.uploaded_at).toLocaleDateString()}
                                            </small>
                                            {!isFormDisabled && (
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0 ms-2 text-danger"
                                                    style={{ textDecoration: 'none' }}
                                                    onClick={() => handleDeleteDocument(existingRes.id, doc.document_id)}
                                                    disabled={deletingDocumentId === doc.document_id || uploadingResponseId === existingRes.id}
                                                >
                                                    {deletingDocumentId === doc.document_id ? (
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"></path><path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"></path></svg>
                                                    )}
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {msg && (
                                <small className={`d-block mt-1 ${msg.type === 'success' ? 'text-success' : 'text-danger'}`}>
                                    {msg.text}
                                </small>
                            )}
                        </div>
                    );
                })()}
            </div>
        );
    };

    if (loading) {
        return <FullPageLoader />;
    }

    if (error) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <Card className="text-center p-5 shadow-lg" style={{ maxWidth: '500px' }}>
                    <Card.Body>
                        <div className="mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="text-danger" viewBox="0 0 16 16">
                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
                            </svg>
                        </div>
                        <h3 className="text-danger mb-3">Assessment Not Found</h3>
                        <p className="text-white mb-0">{error}</p>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    // View Responses Mode (Authenticated)
    if (isViewMode) {
        return (
            <>
                <Container className="mt-4">
                    {/* Assessment Details Card */}
                    {assessment && (
                        <Card className="shadow-lg mb-4 dark-card">
                            <Card.Body className="p-4">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div>
                                        <h3 className="mb-2">Assessment Details</h3>
                                        <p className="text-white mb-0">
                                            {console.log(assessment.last_assessment_grade, 'assessmentassessmentassessment')}
                                            Assessment ID: <strong>#{assessment.id || assessment_id}</strong>
                                        </p>
                                    </div>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={async () => {
                                            setShowPreviousAssessmentModal(true);
                                            setLoadingPreviousAssessments(true);
                                            try {
                                                let query = `?vendor_name=${assessment?.vendor_name}`;
                                                const response = await getTprmAssessmentsService(query);
                                                const data = response?.statusCode?.response_data?.data?.assessments || response?.data?.response_data?.data?.assessments || [];
                                                setPreviousAssessments(Array.isArray(data) ? data : []);
                                            } catch (err) {
                                                console.error('Error fetching previous assessments:', err);
                                                setPreviousAssessments([]);
                                            } finally {
                                                setLoadingPreviousAssessments(false);
                                            }
                                        }}
                                    >
                                        Assessment History
                                    </Button>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <small className="text-white d-block">Vendor Name</small>
                                        <strong>{assessment?.vendor_name || 'N/A'}</strong>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <small className="text-white d-block">Vendor Email</small>
                                        <strong>{assessment?.vendor_email || 'N/A'}</strong>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <small className="text-white d-block">Questionnaire Name</small>
                                        <strong>{assessment?.questionnaire_info?.questionnaire_name || 'N/A'}</strong>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <small className="text-white d-block">Expiry Days</small>
                                        <strong>{assessment?.expiry_days || 'N/A'} days</strong>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <small className="text-white d-block">Created At</small>
                                        <strong>
                                            {assessment?.created_at
                                                ? new Date(assessment.created_at).toLocaleString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })
                                                : 'N/A'
                                            }
                                        </strong>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <small className="text-white d-block">Sent At</small>
                                        <strong>
                                            {assessment?.sent_at
                                                ? new Date(assessment.sent_at).toLocaleString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })
                                                : 'N/A'
                                            }
                                        </strong>
                                    </div>
                                    {assessment?.submitted_at && (
                                        <div className="col-md-6 mb-3">
                                            <small className="text-white d-block">Submitted At</small>
                                            <strong>
                                                {new Date(assessment.submitted_at).toLocaleString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </strong>
                                        </div>
                                    )}
                                    <div className="col-md-6 mb-3">
                                        <small className="text-white d-block">Expires At</small>
                                        <strong>
                                            {assessment?.expires_at
                                                ? new Date(assessment.expires_at).toLocaleString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })
                                                : 'N/A'
                                            }
                                        </strong>
                                    </div>

                                    {assessment && (
                                        <div className="col-md-6 mb-3">
                                            <small className="text-white d-block">Assessment Status:</small>
                                            <strong>{formatText(assessment?.status) || 'N/A'}</strong>
                                        </div>
                                    )}

                                    <div className="col-md-6 mb-3">
                                        <small className="text-white d-block">Assessment Grade</small>
                                        <strong>
                                            {assessment?.grade}
                                        </strong>
                                    </div>
                                </div>


                            </Card.Body>
                        </Card>
                    )}

                    {/* Responses Table Card */}
                    <Card className="shadow-lg dark-card dark_card">
                        <Card.Body className="">


                            {/* Filter and Update Status Section */}
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h3 className="mb-0">Assessment Responses</h3>
                                    {['submitted'].includes(assessment?.status) ? (
                                        <Button
                                            variant="primary"
                                            onClick={() => setShowAssessmentReviewModal(true)}
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            Update Assessment Status
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="secondary"
                                            onClick={() => alert('You cannot update the assessment status. The assessment has already been ' + (assessment?.status?.replace('_', ' ') || 'processed') + '.')}
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            Update Assessment Status
                                        </Button>
                                    )}

                                </div>
                            </div>

                            <div className="d-flex gap-4 align-items-center flex-wrap mb-3">
                                <select
                                    className="form-select w-auto"
                                    value={acceptanceFilter}
                                    onChange={(e) => setAcceptanceFilter(e.target.value)}
                                >
                                    <option value="All">Status</option>
                                    <option value="Accepted">Accepted</option>
                                    <option value="Rejected">Rejected</option>
                                    <option value="Pending">Pending</option>
                                </select>

                                <select
                                    className="form-select w-auto"
                                    value={selectedCategory}
                                    onChange={(e) => {
                                        setSelectedCategory(e.target.value);
                                        setSelectedSubCategory('All');
                                    }}
                                >
                                    <option value="All">Category</option>
                                    {['All', ...new Set(assessmentResponses.map(r => r.question_category).filter(Boolean))].filter(c => c !== 'All').map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>

                                <select
                                    className="form-select w-auto"
                                    value={selectedSubCategory}
                                    onChange={(e) => setSelectedSubCategory(e.target.value)}
                                >
                                    <option value="All">Sub-category</option>
                                    {['All', ...new Set(assessmentResponses
                                        .filter(r => selectedCategory === 'All' || r.question_category === selectedCategory)
                                        .map(r => r.sub_category || r.question_sub_category)
                                        .filter(Boolean))].filter(s => s !== 'All').map(sub => (
                                            <option key={sub} value={sub}>{sub}</option>
                                        ))}
                                </select>

                                <select
                                    className="form-select w-auto"
                                    value={completionFilter}
                                    onChange={(e) => setCompletionFilter(e.target.value)}
                                >
                                    <option value="All">Completion</option>
                                    <option value="Answered">Answered</option>
                                    <option value="Unanswered">Unanswered</option>
                                </select>

                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="mb-1"
                                    onClick={() => {
                                        setSelectedCategory('All');
                                        setSelectedSubCategory('All');
                                        setAcceptanceFilter('All');
                                        setCompletionFilter('All');
                                    }}
                                >
                                    Reset
                                </Button>
                            </div>

                            {(() => {
                                const filteredResponses = assessmentResponses.filter(r => {
                                    const categoryMatch = selectedCategory === 'All' || r.question_category === selectedCategory;
                                    const subCategoryMatch = selectedSubCategory === 'All' || (r.sub_category || r.question_sub_category) === selectedSubCategory;
                                    const acceptanceMatch = acceptanceFilter === 'All' || (r.org_acceptance_status_label || 'Pending') === acceptanceFilter;

                                    const isAnswered = (r.response_text && r.response_text.trim() !== '' && (!Array.isArray(r.response_text) || r.response_text.length > 0)) || (r.explanation && r.explanation.trim() !== '');
                                    const completionMatch = completionFilter === 'All' || (completionFilter === 'Answered' ? isAnswered : !isAnswered);

                                    return categoryMatch && subCategoryMatch && acceptanceMatch && completionMatch;
                                });

                                if (filteredResponses.length === 0) {
                                    return (
                                        <div className="text-center py-5">
                                            <p className="text-white">No responses match your filter criteria.</p>
                                            <Button variant="link" onClick={() => {
                                                setSelectedCategory('All');
                                                setSelectedSubCategory('All');
                                                setAcceptanceFilter('All');
                                                setCompletionFilter('All');
                                            }}>Clear all filters</Button>
                                        </div>
                                    );
                                }

                                return (
                                    <div>
                                        {filteredResponses.map((response, index) => (
                                            <Card key={response.id || index} className="mb-3">
                                                <Card.Body>
                                                    <div className="d-flex gap-2 align-items-center mb-2">
                                                        <span className="color_white fw-semibold font_20">Category Name: </span>
                                                        <p className="text-uppercase text-warning mb-0">{formatText(response.question_sub_category || response.sub_category || response.question_category) || 'N/A'}</p>
                                                    </div>

                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h5 className="color_white fw-semibold mb-1">
                                                            {index + 1}. {response.question_text || 'N/A'}
                                                        </h5>
                                                        <Badge
                                                            className="px-3 py-2 text-nowrap"
                                                            bg={
                                                                (response.org_acceptance_status_label || 'Pending') === 'Accepted' ? 'success' :
                                                                    (response.org_acceptance_status_label || 'Pending') === 'Rejected' ? 'danger' :
                                                                        'warning'
                                                            }
                                                        >
                                                            {response.org_acceptance_status_label || 'Pending'}
                                                        </Badge>
                                                    </div>

                                                    {/* <div className="d-flex gap-2 flex-wrap mb-3">
                                                    <Badge bg="primary" className="px-3 py-2">
                                                        Type: {response.question_type || 'N/A'}
                                                    </Badge>
                                                </div> */}

                                                    {/* Response / Answer */}
                                                    <div className="mb-3">
                                                        <span className="color_white fw-semibold">Answer:</span>
                                                        <div className="mt-1 p-3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                                            {response.response_file_url ? (
                                                                <a
                                                                    href={response.response_file_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-primary"
                                                                >
                                                                    View File
                                                                </a>
                                                            ) : (response.question_type === 'multi_select' || response.question_type === 'multi-select' || response.question_type === 'checkbox') ? (
                                                                <div>
                                                                    {(() => {
                                                                        const selectedOptions = Array.isArray(response.response_text)
                                                                            ? response.response_text
                                                                            : (response.response_text || '').split(',').map(s => s.trim()).filter(Boolean);
                                                                        const allOptions = response.question_multi_select_options || response.options || response.multi_select_options || selectedOptions;
                                                                        return allOptions.map((option, idx) => (
                                                                            <div key={idx} className="form-check mb-2">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="checkbox"
                                                                                    checked={selectedOptions.includes(option)}
                                                                                    disabled
                                                                                    readOnly
                                                                                />
                                                                                <label className="form-check-label text-white">
                                                                                    {option}
                                                                                </label>
                                                                            </div>
                                                                        ));
                                                                    })()}
                                                                </div>
                                                            ) : (response.question_type === 'yes_no' || response.question_type === 'yes/no') ? (
                                                                <div className="d-flex gap-3">
                                                                    <div className="form-check">
                                                                        <input className="form-check-input" type="radio" checked={response.response_text?.toLowerCase() === 'yes'} disabled readOnly />
                                                                        <label className="form-check-label text-white">Yes</label>
                                                                    </div>
                                                                    <div className="form-check">
                                                                        <input className="form-check-input" type="radio" checked={response.response_text?.toLowerCase() === 'no'} disabled readOnly />
                                                                        <label className="form-check-label text-white">No</label>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <span className="text-white">{response.response_text || 'N/A'}</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Explanation */}
                                                    {response.explanation && (
                                                        <div className="mb-3">
                                                            <span className="color_white fw-semibold">Explanation:</span>
                                                            <div className="mt-1 p-3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                                                <span className="text-white">{response.explanation}</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Responded By */}
                                                    {response.responded_by_name && (
                                                        <div className="mb-3 d-flex gap-2 align-items-center">
                                                            <span className="color_white fw-semibold">Reviewed By:</span>
                                                            <span className="text-white">{response.org_reviewed_by_name}</span>
                                                            {response.responded_by_email && (
                                                                <small className="text-white">({response.org_review_notes})</small>
                                                            )}
                                                            {response.responded_at && (
                                                                <small className="text-white ms-2">
                                                                    {new Date(response.org_reviewed_at).toLocaleString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit'
                                                                    })}
                                                                </small>
                                                            )}

                                                        </div>
                                                    )}

                                                    {/* Reviewer Notes */}
                                                    {response.reviewer_notes && (
                                                        <div className="mt-3 p-3 border border-success rounded" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)' }}>
                                                            <div className="d-flex align-items-center mb-2">
                                                                <Badge bg="success" className="me-2">Reviewed</Badge>
                                                            </div>
                                                            <small className="text-white">
                                                                <strong>Reviewer Notes:</strong> {response.reviewer_notes}
                                                            </small>
                                                        </div>
                                                    )}

                                                    {/* Review Button */}
                                                    <div className="d-flex justify-content-end mt-3">
                                                        <Button
                                                            variant={response.is_reviewed ? 'outline-secondary' : 'primary'}
                                                            size="sm"
                                                            onClick={() => handleReviewResponse(response)}
                                                            disabled={reviewing || assessment?.status !== 'submitted'}
                                                        >
                                                            {response.is_reviewed ? 'Update Review' : 'Review'}
                                                        </Button>
                                                    </div>

                                                </Card.Body>
                                            </Card>
                                        ))}
                                    </div>
                                );
                            })()}
                        </Card.Body>
                    </Card>

                    {/* Review Response Modal */}
                    <Modal
                        show={showReviewModal}
                        onHide={() => {
                            setShowReviewModal(false);
                            setSelectedResponse(null);
                            setReviewNotes('');
                            setReviewerType('org');
                            setAcceptanceStatus('pending');
                        }}
                        centered
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Review Response</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {selectedResponse && (
                                <div className="mb-3">
                                    <h6>Question:</h6>
                                    <p className="mb-2">{selectedResponse.question_text || 'N/A'}</p>

                                    <h6>Response:</h6>
                                    <p className="mb-3">
                                        {selectedResponse.response_file_url ? (
                                            <a href={selectedResponse.response_file_url} target="_blank" rel="noopener noreferrer">
                                                View File
                                            </a>
                                        ) : (
                                            selectedResponse.response_text || 'N/A'
                                        )}
                                    </p>
                                </div>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Acceptance Status</Form.Label>
                                <Form.Select
                                    value={acceptanceStatus}
                                    onChange={(e) => setAcceptanceStatus(e.target.value)}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="accepted">Accepted</option>
                                    <option value="rejected">Rejected</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Review Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={reviewNotes}
                                    onChange={(e) => setReviewNotes(e.target.value)}
                                    placeholder="Enter your review notes..."
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowReviewModal(false);
                                    setSelectedResponse(null);
                                    setReviewNotes('');
                                    setReviewerType('org');
                                    setAcceptanceStatus('pending');
                                }}
                                disabled={reviewing}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmitReview}
                                disabled={reviewing}
                            >
                                {reviewing ? 'Reviewing...' : 'Submit Review'}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Update Assessment Review Status Modal */}
                    <Modal
                        show={showAssessmentReviewModal}
                        onHide={() => {
                            setShowAssessmentReviewModal(false);
                            setAssessmentReviewStatus('completed');
                            setAssessmentReviewNotes('');
                            setAssessmentGrade('');
                        }}
                        centered
                        size="lg"
                        className="cstm_modal white_border"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Update Assessment Review Status</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="p-4">
                            <Form.Group className="mb-3">
                                <Form.Label className="text-white">Status <span className="text-danger">*</span></Form.Label>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    {[
                                        { value: 'under_review', label: 'Under Review', color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
                                        { value: 'approved', label: 'Approved', color: '#10B981', bg: 'rgba(16,185,129,0.15)' },
                                        { value: 'rejected', label: 'Rejected', color: '#EF4444', bg: 'rgba(239,68,68,0.15)' },
                                        { value: 'request_resubmission', label: 'Request Resubmission', color: '#8B5CF6', bg: 'rgba(139,92,246,0.15)' },
                                    ].map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setAssessmentReviewStatus(opt.value)}
                                            style={{
                                                padding: '8px 18px',
                                                borderRadius: '8px',
                                                border: assessmentReviewStatus === opt.value ? `2px solid ${opt.color}` : '1px solid rgba(255,255,255,0.15)',
                                                backgroundColor: assessmentReviewStatus === opt.value ? opt.bg : 'transparent',
                                                color: assessmentReviewStatus === opt.value ? opt.color : '#CBD5E1',
                                                fontWeight: assessmentReviewStatus === opt.value ? 600 : 400,
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </Form.Group>

                            {['approved', 'rejected'].includes(assessmentReviewStatus) && (
                                <Form.Group className="mb-3">
                                    {assessment.last_assessment_grade ? <p className="text-white mb-1">Last assessment grade: <strong>{assessment.last_assessment_grade}</strong></p> : ''}
                                    <Form.Label className="text-white">Grade <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        value={assessmentGrade}
                                        onChange={(e) => setAssessmentGrade(e.target.value)}
                                    >
                                        <option value="">Select Grade</option>
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                        <option value="C">C</option>
                                        <option value="D">D</option>
                                        {/* <option value="E">E</option> */}
                                        <option value="F">F</option>
                                    </Form.Select>
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label className="text-white">Review Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={assessmentReviewNotes}
                                    onChange={(e) => setAssessmentReviewNotes(e.target.value)}
                                    placeholder="Enter review notes about the assessment..."
                                />
                            </Form.Group>
                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setShowAssessmentReviewModal(false);
                                        setAssessmentReviewStatus('completed');
                                        setAssessmentReviewNotes('');
                                        setAssessmentGrade('');
                                    }}
                                    disabled={updatingAssessmentStatus}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleUpdateAssessmentStatus}
                                    disabled={updatingAssessmentStatus || !assessmentReviewStatus || (['approved', 'rejected'].includes(assessmentReviewStatus) && !assessmentGrade)}
                                >
                                    {updatingAssessmentStatus ? 'Updating...' : 'Update Status'}
                                </Button>
                            </div>
                        </Modal.Body>

                    </Modal>



                    {/* Previous Assessment Modal */}
                    <Modal
                        show={showPreviousAssessmentModal}
                        onHide={() => setShowPreviousAssessmentModal(false)}
                        centered
                        className='finding_cstm_modal'
                        size="xl"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Assessment History - {assessment?.vendor_name}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {loadingPreviousAssessments ? (
                                <div className="text-center py-4">
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Loading...
                                </div>
                            ) : previousAssessments.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="text-white">No assessment history found.</p>
                                </div>
                            ) : (
                                <CommonTable
                                    headers={[
                                        '#',
                                        'Questionnaire',
                                        'Status',
                                        'Sent At',
                                        'Expires At',
                                        'Days Left',
                                        'Responses',
                                        'Action',
                                    ]}
                                    rows={previousAssessments.map((item, index) => {
                                        const isCurrentAssessment = String(item.id) === String(assessment_id);
                                        return {
                                            id: item.id || index,
                                            rowStyle: isCurrentAssessment ? { backgroundColor: '#1e3a5f', border: '1px solid #3b82f6' } : {},
                                            data: [
                                                index + 1,
                                                <span>
                                                    {item.questionnaire_name || 'Default'}
                                                    {isCurrentAssessment && <Badge bg="info" className="ms-2">Current</Badge>}
                                                </span>,
                                                <Badge bg={getStatusBadge(item.status)}>
                                                    {item.status_display || item.status?.replace('_', ' ').toUpperCase() || 'N/A'}
                                                </Badge>,
                                                item.sent_at ? new Date(item.sent_at).toLocaleDateString() : '-',
                                                item.expires_at ? new Date(item.expires_at).toLocaleDateString() : '-',
                                                item.days_left ?? '-',
                                                `${item.completed_responses}/${item.total_questions}`,
                                                <Button
                                                    size="sm"
                                                    variant={isCurrentAssessment ? "outline-info" : "primary"}
                                                    onClick={() => window.open(`/vendor/assessment/${item.id}/responses`, '_blank')}
                                                >
                                                    {isCurrentAssessment ? 'Current' : 'View'}
                                                </Button>,
                                            ]
                                        };
                                    })}
                                />
                            )}
                        </Modal.Body>

                    </Modal>
                </Container >
            </>
        );
    }

    // Vendor Assessment Mode (Public)
    const allQuestions = [...adminQuestions, ...orgQuestions];

    // Compute unique categories and sub-categories for Vendor Mode
    const vendorCategories = ['All', ...new Set(allQuestions.map(q => q.category).filter(Boolean))];
    const vendorSubCategories = ['All', ...new Set(allQuestions
        .filter(q => selectedCategory === 'All' || q.category === selectedCategory)
        .map(q => q.sub_category)
        .filter(Boolean))];

    const filteredQuestions = allQuestions.filter(q => {
        const categoryMatch = selectedCategory === 'All' || q.category === selectedCategory;
        const subCategoryMatch = selectedSubCategory === 'All' || q.sub_category === selectedSubCategory;

        const sourceType = adminQuestions.some(aq => aq.id === q.id) ? 'admin' : 'org';
        const key = `${sourceType}_${q.id}`;
        const textKey = `${sourceType}_text_${q.id}`;
        const isAnswered = (responses[key] && responses[key] !== '' && (!Array.isArray(responses[key]) || responses[key].length > 0)) || (responses[textKey] && responses[textKey].trim() !== '');

        const statusMatch = completionFilter === 'All' ||
            (completionFilter === 'Answered' ? isAnswered : !isAnswered);

        const aiMatch = !showOnlyAiSuggested || aiSuggestions.some(s =>
            s.question_text?.trim().toLowerCase() === q.question_text?.trim().toLowerCase()
        );

        return categoryMatch && subCategoryMatch && statusMatch && aiMatch;
    });

    const totalQuestions = allQuestions.length;
    const completedResponses = assessment?.completed_responses || 0;

    return (
        <div className='vender_assessment_wraper'>
            <div className='cm_va_header'>
                <img src="https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/small_logo.svg" alt="small logo" height={50} width={50} />
                <div className="logo_wrp"><h3>NMT Security</h3></div>
            </div>
            <div>
                {/* Header Card */}
                <Card className="mb-4 shadow-lg">
                    <Card.Body className="p-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                                <h2 className="mb-2">Vendor Assessment</h2>
                                <p className="text-white mb-0">
                                    From: <strong>{assessment?.created_by_name || 'N/A'}</strong>
                                </p>
                            </div>
                            {assessment?.status !== 'sent' && (
                                <Badge bg={getStatusBadge(assessment?.status)} className="px-3 py-2">
                                    {assessment?.status ? assessment.status.replace('_', ' ').toUpperCase() : 'N/A'}
                                </Badge>
                            )}
                        </div>

                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <small className="text-white d-block">Vendor Name</small>
                                <strong>{assessment?.vendor_name || 'N/A'}</strong>
                            </div>
                            <div className="col-md-6 mb-3">
                                <small className="text-white d-block">Vendor Email</small>
                                <strong>{assessment?.vendor_email || 'N/A'}</strong>
                            </div>
                            <div className="col-md-6 mb-3">
                                <small className="text-white d-block">Questionnaire Name</small>
                                <strong>{assessment?.questionnaire_name || 'Default'}</strong>
                            </div>
                            <div className="col-md-6 mb-3">
                                <small className="text-white d-block">Expires At</small>
                                <strong>
                                    {assessment?.expires_at ? new Date(assessment.expires_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    }) : 'N/A'}
                                </strong>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mt-3">
                            <div className="d-flex justify-content-between mb-2">
                                <small className="text-white">Progress</small>
                                <small className="text-white">{completedResponses} / {totalQuestions} Completed</small>
                            </div>
                            <div className="progress" style={{ height: '10px' }}>
                                <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{ width: `${totalQuestions > 0 ? (completedResponses / totalQuestions) * 100 : 0}%` }}
                                    aria-valuenow={completedResponses}
                                    aria-valuemin="0"
                                    aria-valuemax={totalQuestions}
                                ></div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <div className={`d-flex justify-content-between align-items-center cm_position_sticky${(tutorialStep === 1 || tutorialStep === 2) ? ' tut-toolbar-elevated' : ''}`}>
                    <h4 className="mb-0">
                        Assessment Questions
                        <Badge bg="secondary" className="ms-2">{totalQuestions} Questions</Badge>
                    </h4>
                    <div className="d-flex gap-2">
                        <Button
                            id="tut-ai-btn"
                            variant="primary"
                            onClick={fetchQuestionSuggestions}
                            disabled={loadingSuggestions || isFormDisabled}
                            className={`d-flex align-items-center gap-2${tutorialStep === 2 ? ' tut-zone-active' : ''}`}
                        >
                            {loadingSuggestions ? (
                                <>
                                    <span className="spinner-border spinner-border-sm"></span>
                                    Loading...
                                </>
                            ) : (
                                <>
                                    View AI Suggestions
                                </>
                            )}
                        </Button>
                        <Button
                            id="tut-upload-btn"
                            variant="outline-primary"
                            onClick={() => setShowBulkUploadModal(true)}
                            className={`d-flex align-items-center gap-2${tutorialStep === 1 ? ' tut-zone-active' : ''}`}
                            disabled={isFormDisabled}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                                <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
                            </svg>
                            Upload Documents
                        </Button>


                        <button
                            className="btn btn-lg btn-primary px-5"
                            onClick={handleFinalSubmit}
                            disabled={submitting || saving || isFormDisabled}
                        >
                            {submitting ? 'Submitting...' : 'Submit Assessment'}
                        </button>
                        {/* {saving && (
                            <small className="text-white d-block mt-2">Saving progress...</small>
                        )} */}
                    </div>



                </div>


            </div>

            {/* Filter Bar */}
            <div className="mb-4 px-3">
                <div className="d-flex align-items-center gap-4 flex-wrap">
                    <select
                        className="form-select w-auto"
                        value={selectedCategory}
                        onChange={(e) => {
                            setSelectedCategory(e.target.value);
                            setSelectedSubCategory('All');
                        }}
                    >
                        <option value="All">Category</option>
                        {vendorCategories.filter(cat => cat !== 'All').map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>

                    <select
                        className="form-select w-auto"
                        value={selectedSubCategory}
                        onChange={(e) => setSelectedSubCategory(e.target.value)}
                    >
                        <option value="All">Sub-category</option>
                        {vendorSubCategories.filter(sub => sub !== 'All').map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                        ))}
                    </select>

                    <select
                        className="form-select w-auto"
                        value={completionFilter}
                        onChange={(e) => setCompletionFilter(e.target.value)}
                    >
                        <option value="All">Status</option>
                        <option value="Answered">Answered</option>
                        <option value="Unanswered">Unanswered</option>
                    </select>

                    <div className="d-flex align-items-center gap-2">
                        <label className="mb-0 text-white small">AI Suggested Only</label>
                        <input
                            type="checkbox"
                            checked={showOnlyAiSuggested}
                            onChange={(e) => setShowOnlyAiSuggested(e.target.checked)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                    </div>

                    <Button
                        variant="link"
                        size="sm"
                        className="text-white text-decoration-none p-0"
                        onClick={() => {
                            setSelectedCategory('All');
                            setSelectedSubCategory('All');
                            setCompletionFilter('All');
                            setShowOnlyAiSuggested(false);
                        }}
                    >
                        Clear all filters
                    </Button>
                </div>
            </div>

            {/* Questions Section */}
            <div id="tut-questions" className={tutorialStep === 3 ? 'tut-zone-active' : ''}>
                <Card.Body className="p-3 pt-0">


                    {/* Document Upload Status */}
                    {documentUploads.length > 0 && (
                        <div className=" p-3 mb-3" style={{ backgroundColor: '#1E293B', borderRadius: '8px' }}>
                            <h6 className="text-white mb-2 d-flex align-items-center gap-2">
                                Uploaded Documents Status
                            </h6>
                            <div className="d-flex flex-column gap-2">
                                {documentUploads.map((upload, index) => {
                                    // If document_urls array exists, map through it. Fallback to single entry if empty or not array.
                                    const urls = upload.document_urls && upload.document_urls.length > 0
                                        ? upload.document_urls
                                        : [null]; // Fallback to handle pending/processing state without URL

                                    return urls.map((url, urlIndex) => {
                                        // Extract filename from URL or use fallback
                                        let fileName = 'Processing Document...';
                                        if (url) {
                                            try {
                                                // Handle S3 URLs or normal URLs
                                                const urlParts = url.split('/');
                                                fileName = urlParts[urlParts.length - 1];
                                                // Remove any potential query params or UUID prefix if needed? 
                                                // Usually S3 keys are UUID_filename.ext or similar.
                                                // Let's just show the last part for now, it's safer.
                                            } catch (e) {
                                                fileName = `Document ${index + 1}`;
                                            }
                                        } else if (upload.file_name) {
                                            fileName = upload.file_name;
                                        }

                                        return (
                                            <div
                                                key={`${index}-${urlIndex}`}
                                                className="d-flex justify-content-between align-items-center p-3"
                                                style={{
                                                    backgroundColor: '#0F172A',
                                                    borderRadius: '6px',
                                                    border: '1px solid #334155'
                                                }}
                                            >
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div>
                                                            <div className="text-white fw-medium" style={{ fontSize: '14px' }}>
                                                                {fileName}
                                                            </div>
                                                            <div className="d-flex gap-3 text-white" style={{ fontSize: '12px' }}>
                                                                <span>
                                                                    {formatFileSize(upload.total_size_bytes || 0)}
                                                                </span>
                                                                <span>•</span>
                                                                <span>
                                                                    {new Date(upload.uploaded_at).toLocaleString()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-3">
                                                    {/* {url && (
                                                            <a
                                                                href={url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-sm btn-primary py-1 px-3 d-flex align-items-center gap-2"
                                                                style={{ fontSize: '12px' }}
                                                            >

                                                                View
                                                            </a>
                                                        )} */}
                                                    <span
                                                        className="badge px-3 py-2"
                                                        style={{
                                                            backgroundColor:
                                                                upload.status === 'completed' ? '#10B981' :
                                                                    upload.status === 'processing' ? '#F59E0B' :
                                                                        upload.status === 'failed' ? '#EF4444' : '#6B7280',
                                                            fontSize: '12px',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        {upload.status === 'completed' ? '✓ Completed' :
                                                            upload.status === 'processing' ? '⏳ Processing' :
                                                                upload.status === 'failed' ? '✗ Failed' :
                                                                    upload.status || 'Pending'}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    });
                                })}
                            </div>
                            {processingStatus === 'processing' && (
                                <div className="mt-2 d-flex align-items-center gap-2 text-warning" style={{ fontSize: '13px' }}>
                                    <span className="spinner-border spinner-border-sm"></span>
                                    AI is analyzing your documents. Suggestions will appear soon...
                                </div>
                            )}
                        </div>
                    )}
                    {filteredQuestions.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-white">No questions match your filter criteria.</p>
                            <Button variant="link" onClick={() => {
                                setSelectedCategory('All');
                                setSelectedSubCategory('All');
                                setCompletionFilter('All');
                                setShowOnlyAiSuggested(false);
                            }}>Clear all filters</Button>
                        </div>
                    ) : (
                        <div>
                            {filteredQuestions.map((question, index) => {
                                const sourceType = adminQuestions.some(q => q.id === question.id) ? 'admin' : 'org';
                                return (
                                    <Card key={question.id} className="mb-3">
                                        <Card.Body>

                                            <div className="d-flex gap-2 align-items-center mb-2">
                                                <span className="color_white fw-semibold font_20">Category Name: </span>
                                                <p className="text-uppercase text-warning mb-0">{formatText(question.sub_category || question.category)}</p>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="mb-0">
                                                    {index + 1}. {question.question_text}
                                                </h6>
                                                {question.is_required && (
                                                    <Badge bg="danger" className="ms-2">Required</Badge>
                                                )}
                                            </div>

                                            {question.expected_evidence && (
                                                <p className="mt-2 mb-4 color_cyan">
                                                    <strong>Expected Evidence:</strong> {question.expected_evidence}
                                                </p>
                                            )}

                                            {question.help_text && (
                                                <p className="text-white small mb-2">
                                                    <em>Help: {question.help_text}</em>
                                                </p>
                                            )}

                                            {/* <div className="d-flex gap-2 flex-wrap mt-2">
                                                    <Badge bg="secondary px-3 py-2">
                                                        Weight: {question.weight || 0}
                                                    </Badge>
                                                </div> */}

                                            {/* Answer Input Field */}
                                            <div className="mt-4">
                                                {renderInputField(question, sourceType)}
                                            </div>

                                            {/* Inline AI Suggestion */}
                                            {(() => {
                                                const suggestion = aiSuggestions.find(s =>
                                                    s.question_text?.trim().toLowerCase() === question.question_text?.trim().toLowerCase()
                                                );
                                                const responseKey = `${sourceType}_${question.id}`;
                                                const explanationKey = `${sourceType}_text_${question.id}`;

                                                // Determine which field the suggestion applies to (usually the explanation for structured questions)
                                                const targetKey = ['yes_no', 'yes/no', 'multi_select', 'multi-select'].includes((question.response_type || '').toLowerCase())
                                                    ? explanationKey
                                                    : responseKey;

                                                const currentVal = responses[targetKey] || '';
                                                const isDifferent = (suggestion?.suggested_answer || '').trim() !== String(currentVal || '').trim();

                                                const hasAnswer = responses[responseKey] && responses[responseKey] !== '' && (!Array.isArray(responses[responseKey]) || responses[responseKey].length > 0);
                                                const hasExplanation = responses[explanationKey] && responses[explanationKey].trim() !== '';

                                                if (suggestion && isDifferent) {
                                                    return (
                                                        <div className="mt-2 p-3 border border-primary rounded" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)', borderStyle: 'dashed' }}>
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <h6 className="mb-0 text-info font-monospace">
                                                                    AI Suggestion
                                                                </h6>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Badge bg={suggestion.confidence >= 80 ? 'success' : suggestion.confidence >= 50 ? 'warning' : 'secondary'}>
                                                                        {suggestion.confidence}% Confidence
                                                                    </Badge>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="primary"
                                                                        onClick={() => applySuggestion(question.id, sourceType, suggestion.suggested_answer, question.response_type)}
                                                                        disabled={!suggestion.suggested_answer}
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                            <p className="mb-2 text-white small" style={{ whiteSpace: 'pre-wrap' }}>
                                                                {suggestion.suggested_answer || 'NA'}
                                                            </p>
                                                            {suggestion.relevant_excerpts?.length > 0 && (
                                                                <details className="mb-1">
                                                                    <summary className="text-white small cursor-pointer">
                                                                        View source excerpts
                                                                    </summary>
                                                                    <div className="mt-2 p-2 bg-dark rounded small text-light border border-secondary">
                                                                        {suggestion.relevant_excerpts.map((excerpt, i) => (
                                                                            <div key={i} className="mb-1 fst-italic text-secondary">
                                                                                "{excerpt}"
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </details>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}

                                            {/* Review Info Display */}
                                            {(() => {
                                                const reviewInfo = getReviewInfoForQuestion(question.id, sourceType);
                                                if (reviewInfo) {
                                                    return (
                                                        <div className="mt-3 p-3 border border-success rounded" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)' }}>
                                                            <div className="d-flex align-items-center mb-2">
                                                                <Badge bg="success" className="me-2">Reviewed</Badge>
                                                                {reviewInfo.reviewed_by_name && (
                                                                    <small className="text-success">
                                                                        <strong>Reviewed by:</strong> {reviewInfo.reviewed_by_name}
                                                                    </small>
                                                                )}
                                                            </div>
                                                            {reviewInfo.reviewer_notes && (
                                                                <div className="mt-2">
                                                                    <small className="text-white">
                                                                        <strong>Reviewer Notes:</strong> {reviewInfo.reviewer_notes}
                                                                    </small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}

                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </Card.Body>
            </div>



            {/* Bulk File Upload Modal */}
            <Modal
                show={showBulkUploadModal}
                onHide={() => {
                    setShowBulkUploadModal(false);
                    setBulkFiles([]);
                    setBulkUploadMessage('');
                }}
                centered
                size="lg"
                className="cstm_modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Upload Response Files</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-4">
                        <p className="text-white mb-2 ">
                            Upload files containing your responses to the assessment questions.
                            AI will analyze your documents and suggest answers automatically.
                        </p>
                        <div className="d-flex gap-2 flex-wrap mb-3">
                            <Badge bg="info">Max {MAX_FILES} files</Badge>
                            <Badge bg="info">Max 28MB total</Badge>
                            <Badge bg="secondary">PDF</Badge>
                            <Badge bg="secondary">DOC/DOCX</Badge>
                            <Badge bg="secondary">XLS/XLSX</Badge>
                            <Badge bg="secondary">TXT</Badge>
                        </div>
                        {processingStatus === 'processing' && (
                            <div className="alert alert-info py-2 mb-0 text-dark">
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                AI is analyzing your documents. Suggestions will appear soon...
                            </div>
                        )}
                    </div>

                    {/* File Input */}
                    <div className="mb-4">
                        <Form.Group>
                            <Form.Label className="fw-semibold">Select Files</Form.Label>
                            <Form.Control
                                type="file"
                                multiple
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                                onChange={handleBulkFileSelect}
                                disabled={uploadingBulkFiles || bulkFiles.length >= MAX_FILES}
                            />
                            <Form.Text className="text-white">
                                {bulkFiles.length}/{MAX_FILES} files selected
                                {bulkFiles.length > 0 && ` (Total: ${formatFileSize(bulkFiles.reduce((sum, f) => sum + f.size, 0))})`}
                            </Form.Text>
                        </Form.Group>
                    </div>

                    {/* Selected Files List */}
                    {bulkFiles.length > 0 && (
                        <div className="mb-3">
                            <h6 className="mb-3">Selected Files:</h6>
                            <div className="border rounded p-2" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                {bulkFiles.map((file, index) => (
                                    <div
                                        key={index}
                                        className="d-flex justify-content-between align-items-center p-2 border-bottom"
                                        style={{ backgroundColor: index % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent' }}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge bg-secondary">
                                                {file.name.split('.').pop().toUpperCase()}
                                            </span>
                                            <div>
                                                <div className="fw-medium text-truncate" style={{ maxWidth: '350px' }}>
                                                    {file.name}
                                                </div>
                                                <small className="text-white">{formatFileSize(file.size)}</small>
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeBulkFile(index)}
                                            disabled={uploadingBulkFiles}
                                        >
                                            ✕
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Upload Message */}
                    {bulkUploadMessage && (
                        <div className={`alert text-dark ${bulkUploadMessage.includes('Failed') ? 'alert-danger' : 'alert-success'} mb-0`}>
                            {bulkUploadMessage}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowBulkUploadModal(false);
                            setBulkFiles([]);
                            setBulkUploadMessage('');
                        }}
                        disabled={uploadingBulkFiles}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleBulkUpload}
                        disabled={uploadingBulkFiles || bulkFiles.length === 0}
                    >
                        {uploadingBulkFiles ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Uploading...
                            </>
                        ) : (
                            `Upload ${bulkFiles.length} File${bulkFiles.length !== 1 ? 's' : ''}`
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Toast Notification */}
            {
                toast.show && (
                    <div className={`custom-toast ${toast.type}`}>
                        <span className="icon">
                            {toast.type === 'success' ? '✓' : '✕'}
                        </span>
                        <span className="message">{toast.message}</span>
                        <button
                            className="close-btn"
                            onClick={() => setToast({ show: false, message: '', type: 'success' })}
                        >
                            ×
                        </button>
                    </div>
                )
            }

            {/* Help button — restarts tutorial */}
            {!isViewMode && (
                <button className="tut-help-btn" onClick={() => setTutorialStep(1)} title="Show tutorial">
                    Help
                    <span className="tut-help-icon">?</span>
                </button>
            )}

            {/* Step-by-step Tutorial */}
            {!isViewMode && tutorialStep && (
                <>
                    {/* Dark backdrop — hidden when any modal is open, clickable only on last step */}
                    {!showBulkUploadModal && (
                        <div
                            className={`tut-backdrop${tutorialStep === 3 ? ' tut-backdrop--clickable' : ''}`}
                            onClick={tutorialStep === 3 ? closeTutorial : undefined}
                        />
                    )}

                    {/* Tooltip card anchored to target */}
                    <div
                        className="tut-card"
                        style={{ top: cardPos.top, left: cardPos.left }}
                    >
                        {/* Arrow — up for steps 1&2, down for step 3 */}
                        <div className={`tut-arrow${cardPos.arrowDown ? ' tut-arrow--down' : ''}`} style={{ left: cardPos.arrowLeft }} />

                        <div className="tut-step-header">
                            <span className="tut-step-badge">Step {tutorialStep} of 3</span>
                            <button className="tut-skip" onClick={closeTutorial}>Skip tour</button>
                        </div>
                        <div className="tut-dots">
                            {[1, 2, 3].map(s => (
                                <span key={s} className={`tut-dot${s === tutorialStep ? ' active' : s < tutorialStep ? ' done' : ''}`} />
                            ))}
                        </div>
                        <h5 className="tut-title">{TUTORIAL_STEPS[tutorialStep - 1].title}</h5>
                        <p className="tut-desc">{TUTORIAL_STEPS[tutorialStep - 1].desc}</p>
                        <div className="tut-actions">
                            <button className="tut-next" onClick={advanceTutorial}>
                                {tutorialStep === 3 ? 'Got it!' : 'Next →'}
                            </button>
                        </div>
                    </div>
                </>
            )}

        </div >
    );
}

export default VendorAssessmentView;

